import { getApiKeyFromLocalStorage, getBaseURL } from './apiClient';

export const createSession = async (title = 'New Chat') => {
    const apiKey = getApiKeyFromLocalStorage();
    if (!apiKey) {
      throw new Error('API key is missing');
    }

    const response = await fetch(`${getBaseURL()}/session`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey
        },
        body: JSON.stringify({ title })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to create session: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    if (!responseData.data?.session_id) {
        throw new Error('Invalid session creation response');
    }
    
    return {
        sessionId: responseData.data.session_id,
        ...responseData.data
    };
};

export const getSessions = async (page = 1, limit = 1000) => {
  const apiKey = getApiKeyFromLocalStorage();
  if (!apiKey) {
    throw new Error('API key is missing');
  }

  const url = new URL(`${getBaseURL()}/session`);
  if (page) url.searchParams.append('page', page.toString());
  if (limit) url.searchParams.append('limit', limit.toString());

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'x-api-key': apiKey,
    }
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const getSessionMessages = async (sessionId, page = null, limit = 50) => {
  if (!sessionId) {
    throw new Error('Session ID is required');
  }
  
  const apiKey = getApiKeyFromLocalStorage();
  if (!apiKey) {
    throw new Error('API key is missing');
  }

  const url = new URL(`${getBaseURL()}/session/${sessionId}`);
  if (page !== null) {
    url.searchParams.append('page', page.toString());
  }
  url.searchParams.append('limit', limit.toString());

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'x-api-key': apiKey,
    }
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const sendChatMessage = async (sessionId, message, model, audioFile = null, knowledge_base = true, search = false, tempFileNames = [], abortSignal = null) => {
  if (!sessionId) {
      throw new Error('Valid session ID is required for chat messages');
  }
  
  if (!model) {
      throw new Error('Model is required');
  }

  const formData = new FormData();
  formData.append('session', sessionId);
  formData.append('message', message || (audioFile ? '' : ''));
  formData.append('domain', 'main');
  formData.append('model', model);
  formData.append('knowledge_base', knowledge_base);
  formData.append('search', search);
  
  if (tempFileNames && tempFileNames.length > 0) {
    tempFileNames.forEach(fileName => {
      formData.append('temp_files', fileName);
    });
  }
  
  if (audioFile) {
      if (!(audioFile instanceof File) && !(audioFile instanceof Blob)) {
        console.error('Audio file is not a File or Blob object:', audioFile);
        throw new Error('Invalid audio file object');
      }
      
      formData.append('audio_file', audioFile);
  }

  const fetchOptions = {
      method: 'POST',
      headers: {
          'Accept': 'text/event-stream',
          'x-api-key': getApiKeyFromLocalStorage()
      },
      body: formData
  };

  if (abortSignal) {
    fetchOptions.signal = abortSignal;
  }

  const response = await fetch(`${getBaseURL()}/chat`, fetchOptions);

  if (response.status === 404) {
      throw new Error('Session expired or invalid');
  }

  if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      throw new Error(errorData.error || `Chat request failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const stream = new ReadableStream({
      async start(controller) {
          try {
              while (true) {
                  const {done, value} = await reader.read();
                  if (done) {
                      controller.close();
                      break;
                  }
                  controller.enqueue(value);
              }
          } catch (error) {
              controller.error(error);
          }
      }
  });

  return new Response(stream, {
      headers: {
          'Content-Type': 'text/event-stream'
      }
  });
};

export const sendMessageFeedback = async (messageId, isGood) => {
  const apiKey = getApiKeyFromLocalStorage();
  if (!apiKey) {
    throw new Error('API key is missing');
  }

  const response = await fetch(`${getBaseURL()}/message`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey
    },
    body: JSON.stringify({
      message_id: messageId,
      is_good: isGood
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Feedback submission failed: ${response.status}`);
  }
  
  return response.json();
};

export const generateResponse = async ({ query, model, domain = 'main' }) => {
  try {
    const response = await fetch(`${getBaseURL()}/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': getApiKeyFromLocalStorage()
      },
      body: JSON.stringify({
        query,
        model,
        domain
      })
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized: Please check your API key');
      }
      throw new Error('Failed to generate response');
    }

    return response;
  } catch (error) {
    console.error('Generate response error:', error);
    throw error;
  }
};

export const updateSession = async (sessionId, title, files = []) => {
  const apiKey = getApiKeyFromLocalStorage();
  if (!apiKey) {
    throw new Error('API key is missing');
  }

  if (!sessionId) {
    throw new Error('Session ID is required');
  }

  if (!title) {
    throw new Error('Title is required');
  }

  const response = await fetch(`${getBaseURL()}/session/${sessionId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
    body: JSON.stringify({
      title: title,
      files: files || []
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to update session: ${response.status}`);
  }

  return response.json();
};

export const deleteSession = async (sessionId) => {
  const apiKey = getApiKeyFromLocalStorage();
  if (!apiKey) {
    throw new Error('API key is missing');
  }

  if (!sessionId) {
    throw new Error('Session ID is required');
  }

  const response = await fetch(`${getBaseURL()}/session/${sessionId}`, {
    method: 'DELETE',
    headers: {
      'x-api-key': apiKey,
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to delete session: ${response.status}`);
  }

  return response.json();
};

export const deleteSessions = async (sessionIds) => {
  const apiKey = getApiKeyFromLocalStorage();
  if (!apiKey) {
    throw new Error('API key is missing');
  }

  if (!sessionIds || !Array.isArray(sessionIds) || sessionIds.length === 0) {
    throw new Error('Session IDs array is required');
  }

  const BATCH_SIZE = 50; // Process 50 deletions at a time
  const BATCH_DELAY = 500; // 500ms delay between batches (allows 100 req/s)

  const results = [];
  const totalBatches = Math.ceil(sessionIds.length / BATCH_SIZE);

  for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
    const startIndex = batchIndex * BATCH_SIZE;
    const endIndex = Math.min(startIndex + BATCH_SIZE, sessionIds.length);
    const batchSessionIds = sessionIds.slice(startIndex, endIndex);

    // Process current batch concurrently
    const batchResults = await Promise.allSettled(
      batchSessionIds.map(sessionId => deleteSession(sessionId))
    );

    results.push(...batchResults);

    // Add delay between batches (except for the last batch)
    if (batchIndex < totalBatches - 1) {
      await new Promise(resolve => setTimeout(resolve, BATCH_DELAY));
    }
  }

  const failed = results.filter(r => r.status === 'rejected');
  if (failed.length > 0) {
    console.error('Some deletions failed:', failed);
  }

  return {
    success: results.filter(r => r.status === 'fulfilled').length,
    failed: failed.length,
    total: sessionIds.length
  };
};

